import { Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { BcEmployeeAuthFacade } from '@bayclubs/employee-auth';
import { ApplicationGroupsService, AuthService as HorizonAuthService } from '@bayclubs/horizon';
import { SecurityService } from '@bayclubs/utils';
import { filter, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent {
    private readonly isMsal = environment.isMsalAuthenticationEnabled;
    private readonly horizonAuth = this.isMsal ? inject(HorizonAuthService) : null;
    private readonly securityService = inject(SecurityService);

    // MSAL: BcEmployeeAuthFacade.isAuthorized$ never emits (no token exchange), so the shell is
    // shown once the user is signed in AND privileges are loaded - the same data GcPermissionGuard
    // waits for, so the menubar doesn't flash empty before the menu items can be computed.
    showMenubar = this.isMsal
        ? toSignal(
            toObservable(this.horizonAuth!.isAuthenticated).pipe(
                switchMap(isAuthenticated => isAuthenticated
                    ? this.securityService.getPrivilegesSnapshot$().pipe(map(() => true))
                    : of(false))
            ),
            { initialValue: false }
        )
        : toSignal(this.auth.isAuthorized$, {
            initialValue: this.auth.getIsAuthorizedSnapshot()
        });

    constructor(
        private auth: BcEmployeeAuthFacade,
        private router: Router,
        private readonly destroyRef: DestroyRef,
        private readonly applicationGroupsService: ApplicationGroupsService
    ) {
        this.redirectHashLocationLinksToPathLocation();
        if (!this.isMsal) {
            this.initAuth();
        }
        this.initHorizonProviders();
    }

    private initAuth(): void {
        this.auth.initAuth();
    }

    private redirectHashLocationLinksToPathLocation() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart && /^\/#/.test(event.url)))
            .subscribe(
                (event) => {
                    if (event instanceof NavigationStart) {
                        console.debug('Found path created with HashLocationStrategy. Redirecting to PathLocationStrategy');
                        this.router.navigateByUrl(event.url.replace('/#', ''))
                    }
                }
            );
      }

    private initHorizonProviders(): void {
        if (this.isMsal) {
            effect(() => {
                if (this.horizonAuth!.isAuthenticated()) {
                    this.applicationGroupsService.init();
                }
            });
            return;
        }

        this.auth.token$.pipe(
            map(token => token?.access_token ?? null),
            distinctUntilChanged(),
            filter(Boolean),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.applicationGroupsService.init();
        });
    }
}
